import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { default as Form } from 'antd/es/form';
import { default as Input } from 'antd/es/input';
import Select from 'antd/es/select';
import { useSelector } from 'react-redux';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { useGetCustomerRoles, usePutCustomerUsersUserId } from 'api';
import type { User } from 'api';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import { useOrganisationId } from 'hooks/context';
import { useSafeRoleOptions } from 'hooks/useRolesOptions';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { userManagementDetailsT as translations } from './translations';

import { USER_ROLES_INFO } from '../constants';
import { ResetPasswordButton } from '../shared/ResetPasswordButton';

type Props = {
  data: User;
  onCancelClick: () => void;
  onSuccess: () => void;
};

type UserEditFormValues = {
  first_name: string;
  last_name: string;
  role: string;
  email: string;
};

export const UserDetailsEdit = ({ data, onCancelClick, onSuccess }: Props) => {
  const Label = ({ children }: { children: React.ReactNode }) => (
    <span className="text-sm text-content-secondary">{children}</span>
  );

  const [form] = Form.useForm();
  const organisationId = useOrganisationId<number>();
  const userId = useSelector(selectUserId);
  const openNotification = useOpenNotification();
  const queryClient = useQueryClient();

  const mutation = usePutCustomerUsersUserId();
  const { data: { data: rolesData } = {} } =
    useGetCustomerRoles(organisationId);
  const roles = rolesData || [];
  const selectedRole = Form.useWatch('role', form);

  const { options: safeChoices, isCurrentRoleAssignable } = useSafeRoleOptions({
    roles,
    currentRole: selectedRole
  });

  const onFinish = async (values: UserEditFormValues) => {
    try {
      await mutation.mutateAsync({
        data: { data: values },
        organisationId,
        userId: data.id
      });

      openNotification({
        kind: 'success',
        message: translations.messages.profileUpdated,
        key: 'profile-updated'
      });

      onSuccess();

      queryClient.invalidateQueries({
        queryKey: ['users', organisationId]
      });
    } catch (error: any) {
      const validationErrors = error?.error?.details?.attributes;
      if (validationErrors) {
        form.setFields(
          Object.entries(validationErrors).map(([field, errors]) => ({
            name: field,
            errors: Array.isArray(errors) ? errors : [String(errors)]
          }))
        );
        return;
      }

      console.error('Error updating user:', error);
    }
  };

  if (!userId) return null;

  return (
    <Form
      form={form}
      className="user-management-form"
      onFinish={onFinish}
      layout="vertical"
      autoComplete="off"
      requiredMark={false}
      validateTrigger="onSubmit"
      initialValues={{
        first_name: data.first_name,
        last_name: data.last_name,
        role: data.role,
        email: data.email
      }}
    >
      <div className="flex flex-col">
        <Form.Item
          label={<Label>{translations.fields.firstName}</Label>}
          className="mb-3"
          name="first_name"
          rules={[{ required: true, message: translations.messages.firstName }]}
        >
          <Input placeholder={translations.placeholders.firstName} />
        </Form.Item>

        <Form.Item
          label={<Label>{translations.fields.lastName}</Label>}
          className="mb-3"
          name="last_name"
          rules={[{ required: true, message: translations.messages.lastName }]}
        >
          <Input placeholder={translations.placeholders.lastName} />
        </Form.Item>

        <Form.Item
          label={<Label>{translations.fields.email}</Label>}
          className="mb-3"
          name="email"
        >
          <Input disabled suffix={<LockClosedIcon className="size-5" />} />
        </Form.Item>

        <Form.Item
          label={<Label>{translations.fields.role}</Label>}
          className="mb-3"
          name="role"
          rules={[{ required: true, message: translations.messages.role }]}
        >
          <Select
            options={safeChoices}
            disabled={userId === data.id}
            placeholder={translations.placeholders.role}
          />
        </Form.Item>

        {selectedRole && isCurrentRoleAssignable && (
          <div className="mb-2 rounded-md bg-surface-neutral px-4 py-2">
            {USER_ROLES_INFO[selectedRole.replace(' ', '_')]}
          </div>
        )}

        {!isCurrentRoleAssignable && (
          <span className="ml-2 mt-1 text-xs text-red-500">
            {translations.messages.unassignableRole}
          </span>
        )}

        <div className="mb-2 mt-4">
          {data.email ? <ResetPasswordButton email={data.email} /> : null}
        </div>

        <div className="m-2 border-t" />

        <SecondaryButton
          htmlType="button"
          size="small"
          className="mt-4 h-9"
          onClick={onCancelClick}
        >
          {translations.actions.cancel}
        </SecondaryButton>

        <PrimaryButton
          htmlType="submit"
          size="small"
          className="mt-4 h-9"
          loading={mutation.isPending}
        >
          {translations.actions.save}
        </PrimaryButton>
      </div>
    </Form>
  );
};
